import React, { useEffect, useState } from "react";
import {
  Building2,
  UserCircle2,
  Phone,
  Mail,
  MapPin,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import InstallerLayout from "./InstallerLayout";
import {
  getCurrentInstallerId,
  getInstallerProfile as getInstallerProfileLS,
  saveInstallerProfile as saveInstallerProfileLS,
} from "../../lib/data";
import { supabase } from "../../lib/supabase";

export default function InstallerProfile({ user }) {
  const installerId = getCurrentInstallerId(user);
  const [saved, setSaved] = useState(false);

  const localProfile = getInstallerProfileLS(installerId);

  const [profile, setProfile] = useState(localProfile);
  const [loading, setLoading] = useState(!localProfile); // FIXED
  const [error, setError] = useState("");

  // If Supabase exists, load real profile (core fields) and merge into local profile
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!supabase || !installerId) {
        setLoading(false);
        return;
      }
      try {
        const { data, error: err } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", installerId)
          .single();
        if (err && err.code !== "PGRST116") throw err; // ignore not found
        if (mounted && data) {
          setProfile((p) => ({
            ...p,
            companyName: data.company_name || p.companyName,
            contactName: data.name || p.contactName,
            phone: data.phone || p.phone,
            email: data.email || p.email,
            address: data.address || p.address,
            serviceAreas: data.service_areas || p.serviceAreas,
            rating: data.rating ?? p.rating,
            reviews: data.reviews ?? p.reviews,
            verified: data.verified ?? p.verified,
            experienceYears: data.experience_years ?? p.experienceYears,
            specialties: data.specialties || p.specialties,
            avatar: data.avatar || p.avatar,
          }));
        }
      } catch (e) {
        setError(e.message || "Failed to load profile");
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [installerId]);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!supabase || !installerId) {
        setLoading(false);
        return;
      }

      try {
        const { data, error: err } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", installerId)
          .single();

        if (err && err.code !== "PGRST116") throw err;

        if (mounted && data) {
          setProfile((p) => ({ ...p, ...mapProfile(data, p) }));
        }
      } catch (e) {
        setError(e.message || "Failed to load profile");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    // If we already have a local profile, do NOT show loader, just fetch quietly
    if (!localProfile) setLoading(true);

    load();

    return () => (mounted = false);
  }, []); // DO NOT re-run on every tab switch

  function mapProfile(data, p) {
    return {
      companyName: data.company_name || p.companyName,
      contactName: data.name || p.contactName,
      phone: data.phone || p.phone,
      email: data.email || p.email,
      address: data.address || p.address,
      serviceAreas: data.service_areas || p.serviceAreas,
      rating: data.rating ?? p.rating,
      reviews: data.reviews ?? p.reviews,
      verified: data.verified ?? p.verified,
      experienceYears: data.experience_years ?? p.experienceYears,
      specialties: data.specialties || p.specialties,
      avatar: data.avatar || p.avatar,
    };
  }

  const update = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaved(false);

    // Save to supabase if configured, else localStorage
    if (supabase) {
      try {
        const payload = {
          id: installerId,
          email: profile.email,
          name: profile.contactName,
          company_name: profile.companyName,
          phone: profile.phone,
          address: profile.address,
          service_areas: profile.serviceAreas,
          specialties: profile.specialties,
          experience_years: profile.experienceYears,
          avatar: profile.avatar,
          rating: profile.rating,
          reviews: profile.reviews,
          verified: profile.verified,
          updated_at: new Date().toISOString(),
        };
        const { error: err } = await supabase
          .from("profiles")
          .upsert(payload, { onConflict: "id" });
        if (err) throw err;
        // Also persist enhanced fields locally
        const next = saveInstallerProfileLS(installerId, profile);
        setProfile(next);
        setSaved(true);
        setTimeout(() => setSaved(false), 1500);
      } catch (e) {
        setError(e.message || "Failed to save profile");
      }
    } else {
      const next = saveInstallerProfileLS(installerId, profile);
      setProfile(next);
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    }
  };

  const handleLogout = async () => {
    const confirmLogout = window.confirm("Are you sure you want to logout?");
    if (!confirmLogout) return;

    try {
      // Supabase logout
      await supabase.auth.signOut({ scope: "global" });

      // Remove localStorage keys used by your custom installer logic
      localStorage.removeItem("rb_current_installer"); // CURRENT_INSTALLER
      localStorage.removeItem("rb_installers"); // INSTALLERS

      // Optionally, clear other app data
      localStorage.removeItem("rb_jobs");
      localStorage.removeItem("rb_bids");

      sessionStorage.clear();

      // Clear any Supabase cookies (just in case)
      document.cookie.split(";").forEach((cookie) => {
        document.cookie =
          cookie.trim().split("=")[0] +
          "=;expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      });

      // Force reload
      window.location.replace("/login");
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  return (
    <InstallerLayout>
      <div className="space-y-6" data-testid="installer-profile-page">
        {/* Header card */}
        <section className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-blue-600 to-emerald-500 text-white p-5 md:p-6">
          <div className="absolute inset-0 opacity-25 bg-[radial-gradient(circle_at_0%_0%,#ffffff55_0,transparent_45%),radial-gradient(circle_at_100%_0,#ffffff33_0,transparent_45%)]" />
          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center border border-white/20">
                  {profile.avatar ? (
                    <img
                      src={profile.avatar}
                      alt={profile.contactName}
                      className="w-full h-full rounded-2xl object-cover"
                    />
                  ) : (
                    <UserCircle2 className="w-10 h-10 text-blue-50" />
                  )}
                </div>
                {profile.verified && (
                  <span className="absolute -bottom-1 -right-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500 border-2 border-indigo-600 text-white text-xs">
                    <ShieldCheck className="w-3 h-3" />
                  </span>
                )}
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-blue-100 flex items-center gap-2">
                  <Building2 className="w-4 h-4" /> Installer profile
                </p>
                <h1 className="mt-1 text-xl md:text-2xl font-bold">
                  {profile.companyName || "Your roofing company"}
                </h1>
                <p className="text-xs md:text-sm text-blue-100 flex flex-wrap items-center gap-2 mt-1">
                  <span>
                    {profile.contactName || "Add a primary contact name"}
                  </span>
                  <span className="opacity-60">•</span>
                  <span>
                    {profile.serviceAreas?.join(", ") ||
                      "Add service areas to stand out"}
                  </span>
                </p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 md:w-72">
              <div className="bg-white/10 rounded-xl p-3 text-center">
                <div className="text-[11px] text-blue-100">Rating</div>
                <div className="text-lg font-semibold flex items-center justify-center gap-1">
                  {(profile.rating ?? 0).toFixed(1)}
                  <span className="text-[10px] font-normal text-blue-100">
                    ({profile.reviews ?? 0})
                  </span>
                </div>
              </div>
              <div className="bg-white/10 rounded-xl p-3 text-center">
                <div className="text-[11px] text-blue-100">Experience</div>
                <div className="text-lg font-semibold">
                  {profile.experienceYears ?? 0}y
                </div>
              </div>
              <div className="bg-white/10 rounded-xl p-3 text-center">
                <div className="text-[11px] text-blue-100">Completed</div>
                <div className="text-lg font-semibold">
                  {profile.completedJobs ?? 0}
                </div>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-red-700 shadow-sm"
            >
              Logout
            </button>
          </div>
        </section>

        {error && (
          <div
            className="mb-3 bg-red-50 text-red-700 border border-red-200 px-3 py-2 rounded-lg text-sm"
            data-testid="profile-error"
          >
            {error}
          </div>
        )}

        {saved && (
          <div
            className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 px-3 py-2 rounded-xl"
            data-testid="profile-saved"
          >
            <ShieldCheck className="w-4 h-4" /> Profile updated
          </div>
        )}

        {loading ? (
          <div
            className="py-12 text-center text-gray-500 bg-white rounded-2xl border border-gray-100"
            data-testid="profile-loading"
          >
            Loading profile…
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6 items-start">
            {/* Form */}
            <form
              onSubmit={onSubmit}
              className="md:col-span-2 space-y-4 bg-white rounded-2xl p-5 shadow-sm border border-gray-100"
            >
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-indigo-500" /> Public
                  profile details
                </h2>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Company Name
                  </label>
                  <input
                    data-testid="company-name"
                    value={profile.companyName || ""}
                    onChange={(e) => update("companyName", e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Contact Name
                  </label>
                  <input
                    data-testid="contact-name"
                    value={profile.contactName || ""}
                    onChange={(e) => update("contactName", e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
                    <Mail className="w-3 h-3 text-gray-400" /> Email
                  </label>
                  <input
                    data-testid="contact-email"
                    type="email"
                    value={profile.email || ""}
                    onChange={(e) => update("email", e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
                    <Phone className="w-3 h-3 text-gray-400" /> Phone
                  </label>
                  <input
                    data-testid="contact-phone"
                    value={profile.phone || ""}
                    onChange={(e) => update("phone", e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-gray-400" /> Address
                </label>
                <input
                  data-testid="company-address"
                  value={profile.address || ""}
                  onChange={(e) => update("address", e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Service Areas (comma separated)
                  </label>
                  <input
                    data-testid="service-areas"
                    value={(profile.serviceAreas || []).join(", ")}
                    onChange={(e) =>
                      update(
                        "serviceAreas",
                        e.target.value
                          .split(",")
                          .map((s) => s.trim())
                          .filter(Boolean)
                      )
                    }
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Specialties (comma separated)
                  </label>
                  <input
                    data-testid="specialties"
                    value={(profile.specialties || []).join(", ")}
                    onChange={(e) =>
                      update(
                        "specialties",
                        e.target.value
                          .split(",")
                          .map((s) => s.trim())
                          .filter(Boolean)
                      )
                    }
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Years of Experience
                  </label>
                  <input
                    data-testid="experience-years"
                    type="number"
                    min="0"
                    value={profile.experienceYears || 0}
                    onChange={(e) =>
                      update("experienceYears", Number(e.target.value || 0))
                    }
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Completed Jobs
                  </label>
                  <input
                    data-testid="installer-completed-jobs"
                    type="number"
                    min="0"
                    value={profile.completedJobs || 0}
                    onChange={(e) =>
                      update("completedJobs", Number(e.target.value || 0))
                    }
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Rating (1–5)
                  </label>
                  <input
                    data-testid="installer-rating"
                    type="number"
                    min="1"
                    max="5"
                    step="0.1"
                    value={profile.rating ?? ""}
                    onChange={(e) =>
                      update("rating", Number(e.target.value || 0))
                    }
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Reviews
                  </label>
                  <input
                    data-testid="installer-reviews"
                    type="number"
                    min="0"
                    value={profile.reviews || 0}
                    onChange={(e) =>
                      update("reviews", Number(e.target.value || 0))
                    }
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Base Rate ($/sq ft)
                  </label>
                  <input
                    data-testid="installer-base-rate"
                    type="number"
                    min="0"
                    step="0.01"
                    value={profile.baseRateSqFt ?? ""}
                    onChange={(e) =>
                      update("baseRateSqFt", Number(e.target.value || 0))
                    }
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Typical Timeline
                  </label>
                  <input
                    data-testid="installer-base-timeline"
                    value={profile.baseTimeline || ""}
                    onChange={(e) => update("baseTimeline", e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Avatar URL
                  </label>
                  <input
                    data-testid="avatar-url"
                    value={profile.avatar || ""}
                    onChange={(e) => update("avatar", e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    What&apos;s Included (comma separated)
                  </label>
                  <input
                    data-testid="installer-whats-included"
                    value={(profile.whatsIncluded || []).join(", ")}
                    onChange={(e) =>
                      update(
                        "whatsIncluded",
                        e.target.value
                          .split(",")
                          .map((s) => s.trim())
                          .filter(Boolean)
                      )
                    }
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Short Bio
                </label>
                <textarea
                  data-testid="installer-bio"
                  rows={3}
                  value={profile.bio || ""}
                  onChange={(e) => update("bio", e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex items-center justify-end pt-2">
                <button
                  data-testid="save-profile"
                  type="submit"
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-blue-700 shadow-sm"
                >
                  Save Profile
                </button>
              </div>
            </form>

            {/* Preview card */}
            <div
              className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100"
              data-testid="installer-profile-preview-card"
            >
              <div className="flex items-center space-x-4 mb-4">
                <img
                  src={profile.avatar}
                  alt={profile.contactName}
                  className="w-16 h-16 rounded-full object-cover border-2 border-gray-100"
                />
                <div>
                  <div className="text-lg font-bold text-gray-900">
                    {profile.companyName}
                  </div>
                  <div className="text-sm text-gray-600">
                    {profile.contactName}
                  </div>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className="text-sm font-semibold">
                      {(profile.rating ?? 0).toFixed(1)}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({profile.reviews ?? 0} reviews)
                    </span>
                    {profile.verified && (
                      <span className="text-xs text-emerald-600 font-semibold">
                        ✓ Verified
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 mb-4 text-center">
                <div className="text-lg font-bold text-blue-600">
                  ${Number(profile.baseRateSqFt || 0).toFixed(2)}/sq ft
                </div>
                <div className="text-xs text-gray-500 mb-2">
                  Rate per square foot
                </div>
                <div className="text-sm text-gray-600">
                  Typical timeline: {profile.baseTimeline || "4-6 days"}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 text-sm mb-4">
                <div>
                  <div className="text-gray-500 text-xs">Experience</div>
                  <div className="font-semibold text-gray-900">
                    {profile.experienceYears ?? 0} years
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 text-xs">Completed</div>
                  <div className="font-semibold text-gray-900">
                    {profile.completedJobs ?? 0} jobs
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 text-xs">Specialties</div>
                  <div className="font-semibold text-gray-900 line-clamp-1">
                    {(profile.specialties || []).join(", ")}
                  </div>
                </div>
              </div>

              <div>
                <div className="text-sm font-semibold text-gray-900 mb-2">
                  What&apos;s Included
                </div>
                <ul className="space-y-1 text-xs text-gray-700">
                  {(profile.whatsIncluded || []).map((item, idx) => (
                    <li key={idx}>• {item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </InstallerLayout>
  );
}
